import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CoreLayout from "../../components/layout/CoreLayout";
import Modal from "../../components/Modal";
import PageContainer from "../../components/PageContainer";
import PageHeader from "../../components/PageHeader";
import { cardBorderClassName } from "../../lib/cardStyles";
import {
  assignedVoiceSummary,
  CATALOG_VOICE_ID_SET,
  CUSTOM_VOICE_SELECT,
  ELEVENLABS_VOICE_CATALOG,
} from "../../lib/elevenLabsVoices";
import { formatDateTime } from "../../lib/formatDateTime";
import { usePageTitle } from "../../lib/usePageTitle";
import {
  VOICE_STABILITY_LABEL_LEFT,
  VOICE_STABILITY_LABEL_RIGHT,
  canSaveVoiceSettings,
  parseVoiceSettingsFromServer,
  type HydratedVoiceSettings,
} from "../../lib/voiceSettingsSsot";
import {
  canSaveBusinessTimezone,
  listBusinessTimezoneOptionGroups,
  parseBusinessTimezoneFromConfig,
  timezoneSelectValue,
} from "../../lib/businessTimezoneSsot";

// -----------------------------------------------------------------------------
// Backend API base
// -----------------------------------------------------------------------------
const API_BASE =
  import.meta.env.VITE_API_URL || "https://app.neurospherevoiceai.com/api";

const PHONE_PROVISIONING_PLACEHOLDER =
  "We're setting up your dedicated phone number — you'll get an email within one business day with your number and simple instructions for forwarding your existing business line to it. Your customers keep calling the number they already know; your AI receptionist answers.";

const PHONE_FORWARDING_HINT =
  "Forward your existing business line to this number to put your receptionist to work. Forwarding instructions were included in your activation email.";

// -----------------------------------------------------------------------------
// Slider / personality types and constants (from personality-studio)
// -----------------------------------------------------------------------------
type BlockKey =
  | "system_role"
  | "emotional_tone"
  | "knowledge_context"
  | "safety_boundaries";

type SliderKey =
  | "warmth" | "formality" | "humor" | "directness"
  | "empathy" | "confidence" | "curiosity" | "patience"
  | "creativity" | "analytical" | "storytelling" | "detail"
  | "assertiveness" | "humility" | "optimism" | "sarcasm"
  | "formalityShift" | "inclusive" | "memory" | "risk"
  | "selfReference" | "topicFocus" | "repetition" | "polish"
  | "intensity" | "caution" | "jargon" | "humorSensitivity"
  | "consistency" | "metaAwareness";

const SLIDER_KEYS: SliderKey[] = [
  "warmth","formality","humor","directness",
  "empathy","confidence","curiosity","patience",
  "creativity","analytical","storytelling","detail",
  "assertiveness","humility","optimism","sarcasm",
  "formalityShift","inclusive","memory","risk",
  "selfReference","topicFocus","repetition","polish",
  "intensity","caution","jargon","humorSensitivity",
  "consistency","metaAwareness",
];

const DEFAULT_50: Record<SliderKey, number> = SLIDER_KEYS.reduce((acc, k) => {
  acc[k] = 50;
  return acc;
}, {} as Record<SliderKey, number>);

const PRESETS: Record<string, Record<SliderKey, number>> = {
  professional: {
    ...DEFAULT_50,
    warmth: 60, formality: 80, humor: 20, directness: 70,
    empathy: 65, confidence: 85, patience: 80, analytical: 80,
    assertiveness: 70, polish: 90, caution: 70, topicFocus: 80,
    inclusive: 85, memory: 85,
  },
  friendly: {
    ...DEFAULT_50,
    warmth: 90, formality: 40, humor: 70, directness: 60,
    empathy: 85, confidence: 70, curiosity: 70, patience: 85,
    storytelling: 70, optimism: 85, jargon: 30, polish: 65,
  },
  assertive: {
    ...DEFAULT_50,
    directness: 90, confidence: 95, assertiveness: 95,
    warmth: 50, empathy: 50, patience: 50, risk: 70, topicFocus: 85,
  },
  empathetic: {
    ...DEFAULT_50,
    warmth: 90, empathy: 95, patience: 95, inclusive: 95,
    humor: 50, confidence: 65, optimism: 80, humorSensitivity: 85,
  },
  flirtatious: {
    ...DEFAULT_50,
    warmth: 95, humor: 85, optimism: 90, intensity: 75,
    formality: 15, directness: 45, risk: 65, caution: 30,
  },
  balanced: { ...DEFAULT_50 },
  concierge: {
    ...DEFAULT_50,
    warmth: 85, formality: 60, empathy: 80, patience: 85,
    polish: 80, topicFocus: 70, confidence: 75, humor: 45,
  },
  highEnergySales: {
    ...DEFAULT_50,
    warmth: 75, confidence: 90, assertiveness: 90, directness: 80,
    optimism: 90, intensity: 85, humor: 55, caution: 45,
  },
};

const PRESET_LABELS: Record<string, string> = {
  professional: "Professional",
  friendly: "Friendly",
  assertive: "Assertive",
  empathetic: "Empathetic",
  flirtatious: "Playful",
  balanced: "Balanced",
  concierge: "Concierge",
  highEnergySales: "High-energy sales",
};

type SaveKey =
  | "agentName"
  | "timezone"
  | "newGreeting"
  | "existingGreeting"
  | "voice"
  | "system_role"
  | "emotional_tone"
  | "knowledge_context"
  | "sliders"
  | "safety_boundaries";

type SaveAllStep = "system_role" | "emotional_tone" | "knowledge_context" | "sliders";

const SAVE_ALL_STEPS: SaveAllStep[] = ["system_role", "emotional_tone", "knowledge_context", "sliders"];

// -----------------------------------------------------------------------------
// Tab definition
// -----------------------------------------------------------------------------
type TabId = "agent" | "greetings" | "voice" | "personality" | "safeties";

const VALID_TAB_IDS = new Set<TabId>([
  "agent",
  "greetings",
  "voice",
  "personality",
  "safeties",
]);

function tabFromSearchParams(params: URLSearchParams): TabId | null {
  const tab = params.get("tab");
  return tab && VALID_TAB_IDS.has(tab as TabId) ? (tab as TabId) : null;
}

const TABS: { id: TabId; label: string }[] = [
  { id: "agent",       label: "Agent" },
  { id: "greetings",   label: "Greetings" },
  { id: "voice",       label: "Voice" },
  { id: "personality", label: "Personality" },
  { id: "safeties",    label: "Safety Rules" },
];

// -----------------------------------------------------------------------------
// Shared sub-components
// -----------------------------------------------------------------------------
function CardShell({
  icon,
  title,
  children,
}: {
  icon: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`p-6 rounded-2xl backdrop-blur-sm ${cardBorderClassName}`}
      style={{
        background: "rgba(26, 26, 36, 0.75)",
      }}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-purple-600 to-orange-600">
          <i className={`${icon} text-white text-xl`} />
        </div>
        <h2
          className="text-xl font-semibold text-white"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {title}
        </h2>
      </div>
      {children}
    </div>
  );
}

function isError(msg: string) {
  return msg.includes("Error") || msg.includes("Failed") || msg.startsWith("❌");
}

function SaveMessage({ message, className = "mt-2" }: { message?: string | null; className?: string }) {
  const tone = message ? (isError(message) ? "text-red-300" : "text-green-300") : "";
  return <p className={`${className} min-h-[1.25rem] text-sm ${tone}`}>{message || null}</p>;
}

function SaveButton({
  onClick,
  disabled,
  saving,
  label,
  savingLabel = "Saving...",
  message,
}: {
  onClick: () => void;
  disabled: boolean;
  saving: boolean;
  label: string;
  savingLabel?: string;
  message?: string | null;
}) {
  return (
    <>
      <button
        onClick={onClick}
        disabled={disabled}
        className="mt-4 w-full py-3 rounded-lg font-semibold transition-all"
        style={{
          background: disabled
            ? "rgba(138, 43, 226, 0.4)"
            : "linear-gradient(90deg, #8a2be2, #ff6a00)",
          color: "white",
          cursor: disabled ? "not-allowed" : "pointer",
          fontFamily: "'Space Grotesk', sans-serif",
        }}
      >
        {saving ? savingLabel : label}
      </button>
      <SaveMessage message={message} />
    </>
  );
}

function SliderRow({
  k,
  label,
  value,
  onChange,
}: {
  k: SliderKey;
  label: string;
  value: number;
  onChange: (k: SliderKey, v: number) => void;
}) {
  return (
    <div
      className="p-4 rounded-lg"
      style={{
        background: "rgba(0,0,0,0.35)",
        border: "1px solid rgba(138, 43, 226, 0.12)",
      }}
    >
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-200">{label}</div>
        <div className="text-sm" style={{ color: "#fbbf24" }}>
          {value}
        </div>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        value={value}
        onChange={(e) => onChange(k, Number(e.target.value))}
        className="w-full mt-3"
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
const parseJsonOrThrow = async <T,>(res: Response): Promise<T> => {
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (ct.includes("application/json")) {
    return (await res.json()) as T;
  }
  const txt = await res.text().catch(() => "");
  const preview = txt.slice(0, 240).replace(/\s+/g, " ").trim();
  throw new Error(
    `Expected JSON but got "${ct || "unknown"}". Preview: ${preview || "(empty)"}`
  );
};

function trimStr(v: unknown): string {
  if (typeof v === "string") return v.trim();
  if (v == null) return "";
  return String(v).trim();
}

/** Mirror ChatStack packer._extract_agent_name field order. */
function extractAgentName(config: Record<string, unknown>): string {
  const agent =
    config.agent && typeof config.agent === "object"
      ? (config.agent as Record<string, unknown>)
      : undefined;
  for (const c of [agent?.agent_name, agent?.name, config.agentName, config.agent_name]) {
    const s = trimStr(c);
    if (s) return s;
  }
  return "";
}

function extractGreetingFromConfig(
  config: Record<string, unknown>,
  which: "new" | "existing",
): string {
  const agent =
    config.agent && typeof config.agent === "object"
      ? (config.agent as Record<string, unknown>)
      : undefined;
  const greetings =
    config.greetings && typeof config.greetings === "object"
      ? (config.greetings as Record<string, unknown>)
      : undefined;
  const greetingTemplate =
    config.greeting_template && typeof config.greeting_template === "object"
      ? (config.greeting_template as Record<string, unknown>)
      : undefined;
  const phone =
    config.phone && typeof config.phone === "object"
      ? (config.phone as Record<string, unknown>)
      : undefined;
  const phoneGreetingTemplate =
    phone?.greeting_template && typeof phone.greeting_template === "object"
      ? (phone.greeting_template as Record<string, unknown>)
      : undefined;

  if (which === "new") {
    return trimStr(
      agent?.new_caller_greeting ??
        greetings?.new_caller_greeting ??
        greetingTemplate?.new ??
        phoneGreetingTemplate?.new,
    );
  }
  return trimStr(
    agent?.existing_user_greeting ??
      greetings?.existing_user_greeting ??
      greetingTemplate?.existing ??
      phoneGreetingTemplate?.existing,
  );
}

function promptsFromConfig(config: Record<string, unknown>): Record<BlockKey, string> {
  const prompts =
    config.prompts && typeof config.prompts === "object"
      ? (config.prompts as Record<string, unknown>)
      : {};
  const systemPrompts =
    config.system_prompts && typeof config.system_prompts === "object"
      ? (config.system_prompts as Record<string, unknown>)
      : {};
  return {
    system_role: trimStr(
      prompts.main_system_prompt ?? prompts.system_prompt ?? systemPrompts.main,
    ),
    emotional_tone: trimStr(
      prompts.mood_prompt ?? prompts.mood ?? systemPrompts.mood,
    ),
    knowledge_context: trimStr(
      prompts.knowledge_prompt ?? prompts.knowledge ?? systemPrompts.knowledge,
    ),
    safety_boundaries: trimStr(
      prompts.safety_prompt ?? prompts.safety ?? systemPrompts.safety,
    ),
  };
}

const EMPTY_BLOCKS: Record<BlockKey, string> = {
  system_role: "",
  emotional_tone: "",
  knowledge_context: "",
  safety_boundaries: "",
};

function LoadGuardNotice({ failed }: { failed: boolean }) {
  if (!failed) return null;
  return (
    <p
      className="text-xs text-amber-400/90 mt-2"
      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
    >
      Couldn&apos;t load saved value — reload before editing.
    </p>
  );
}

// -----------------------------------------------------------------------------
// Main page
// -----------------------------------------------------------------------------
export default function PhoneSystemApp() {
  usePageTitle("Phone System");
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabId>(
    () => tabFromSearchParams(searchParams) ?? "agent",
  );

  useEffect(() => {
    const tab = tabFromSearchParams(searchParams);
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  // ── Agent tab ──
  const [agentName, setAgentName] = useState("");
  const [agentRole, setAgentRole] = useState("");
  const [businessTimezone, setBusinessTimezone] = useState<string | null>(null);
  const [businessTimezoneHydrated, setBusinessTimezoneHydrated] = useState(false);
  const [businessTimezoneLoadError, setBusinessTimezoneLoadError] = useState<
    string | null
  >(null);
  const [twilioNumber, setTwilioNumber] = useState("");
  const [openaiVoice, setOpenaiVoice] = useState("");

  // ── Greetings tab ──
  const [newCallerGreeting, setNewCallerGreeting] = useState("");
  const [existingGreeting, setExistingGreeting] = useState("");
  /** Last-loaded server values — used when saving one greeting without clobbering the other. */
  const [serverNewCallerGreeting, setServerNewCallerGreeting] = useState("");
  const [serverExistingGreeting, setServerExistingGreeting] = useState("");

  // ── Voice tab (hydrate ONLY from GET /voice — no fake persisted defaults) ──
  const [elevenVoiceId, setElevenVoiceId] = useState("");
  const [voiceStability, setVoiceStability] = useState<number | null>(null);
  const [voiceSimilarity, setVoiceSimilarity] = useState<number | null>(null);
  const [voiceSettingsUpdatedAt, setVoiceSettingsUpdatedAt] = useState<string | null>(
    null,
  );
  const [voiceSettingsLoadError, setVoiceSettingsLoadError] = useState<string | null>(
    null,
  );
  const [voiceSettingsLoading, setVoiceSettingsLoading] = useState(false);

  // ── Personality / Safeties tabs ──
  const [serverBlocks, setServerBlocks] = useState<Record<string, string>>({});
  const [promptMain, setPromptMain] = useState("");
  const [promptMood, setPromptMood] = useState("");
  const [promptKnowledge, setPromptKnowledge] = useState("");
  const [promptSafety, setPromptSafety] = useState("");
  const [preset, setPreset] = useState<string>("balanced");
  const [sliders, setSliders] = useState<Record<SliderKey, number>>({ ...DEFAULT_50 });
  const [showAdvanced, setShowAdvanced] = useState(false);

  // ── Loading ──
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [configLoadOk, setConfigLoadOk] = useState(false);
  const [voiceLoadOk, setVoiceLoadOk] = useState(false);
  const [promptsLoadOk, setPromptsLoadOk] = useState(false);

  // ── Saving flags ──
  const [isSavingAgent, setIsSavingAgent] = useState(false);
  const [isSavingTimezone, setIsSavingTimezone] = useState(false);
  const [isSavingNewGreeting, setIsSavingNewGreeting] = useState(false);
  const [isSavingExistingGreeting, setIsSavingExistingGreeting] = useState(false);
  const [isSavingVoice, setIsSavingVoice] = useState(false);
  const [isSavingField, setIsSavingField] = useState<BlockKey | null>(null);
  const [isSavingSliders, setIsSavingSliders] = useState(false);

  // ── Flash ──
  const [saveMessage, setSaveMessage] = useState("");
  const [lastSavedKey, setLastSavedKey] = useState<SaveKey | null>(null);
  const messageFor = (key: SaveKey) => (lastSavedKey === key ? saveMessage : null);

  // ── Save all (Personality) ──
  const [saveAllQueue, setSaveAllQueue] = useState<SaveAllStep[]>([]);
  const saveAllInFlightRef = useRef<SaveAllStep[] | null>(null);

  // ── AI Generate modal ──
  const [generateModalOpen, setGenerateModalOpen] = useState<string | null>(null);
  const generateButtonRef = useRef<HTMLElement | null>(null);
  const [generateBizType, setGenerateBizType] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------
  const getCustomerId = () =>
    localStorage.getItem("customerId") || sessionStorage.getItem("customerId");

  const flash = (msg: string) => {
    setSaveMessage(msg);
    window.setTimeout(() => setSaveMessage(""), 3000);
  };

  const markVoiceUnloaded = (errorMsg: string | null = null) => {
    setVoiceLoadOk(false);
    setVoiceStability(null);
    setVoiceSimilarity(null);
    setVoiceSettingsUpdatedAt(null);
    setVoiceSettingsLoadError(errorMsg);
  };

  const applyHydratedVoice = (hydrated: HydratedVoiceSettings) => {
    setElevenVoiceId(hydrated.voiceId);
    setVoiceStability(hydrated.stability);
    setVoiceSimilarity(hydrated.similarity);
    setVoiceSettingsUpdatedAt(hydrated.updatedAt);
    setVoiceLoadOk(true);
    setVoiceSettingsLoadError(null);
  };

  /** Canonical voice hydrate — never invents 0.71/0.5 editable defaults. */
  const loadVoiceSettings = async (): Promise<boolean> => {
    const customerId = getCustomerId();
    if (!customerId) {
      markVoiceUnloaded("Unable to load saved voice settings. Retry.");
      return false;
    }
    setVoiceSettingsLoading(true);
    setVoiceSettingsLoadError(null);
    try {
      const voiceRes = await fetch(`${API_BASE}/customers/${customerId}/voice`, {
        method: "GET",
        credentials: "include",
      });
      if (!voiceRes.ok) {
        markVoiceUnloaded("Unable to load saved voice settings. Retry.");
        return false;
      }
      const voiceJson = await voiceRes.json();
      const hydrated = parseVoiceSettingsFromServer(voiceJson?.settings);
      if (!hydrated) {
        markVoiceUnloaded("Unable to load saved voice settings. Retry.");
        return false;
      }
      applyHydratedVoice(hydrated);
      return true;
    } catch {
      markVoiceUnloaded("Unable to load saved voice settings. Retry.");
      return false;
    } finally {
      setVoiceSettingsLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Load
  // ---------------------------------------------------------------------------
  const loadAll = async () => {
    setIsLoadingConfig(true);
    setLoadError(null);
    setConfigLoadOk(false);
    setVoiceLoadOk(false);
    setPromptsLoadOk(false);
    markVoiceUnloaded(null);
    setElevenVoiceId("");
    setBusinessTimezone(null);
    setBusinessTimezoneHydrated(false);
    setBusinessTimezoneLoadError(null);

    try {
      const customerId = getCustomerId();
      if (!customerId) {
        console.warn("No customer ID found");
        return;
      }

      const [configRes, voiceRes, blocksRes] = await Promise.all([
        fetch(`${API_BASE}/customers/${customerId}/config`, {
          method: "GET",
          credentials: "include",
        }),
        fetch(`${API_BASE}/customers/${customerId}/voice`, {
          method: "GET",
          credentials: "include",
        }),
        fetch(`${API_BASE}/prompt-blocks/load`, {
          method: "GET",
          credentials: "include",
        }),
      ]);

      let config: Record<string, unknown> | null = null;
      let configOk = false;

      // ── Config ──
      if (configRes.ok) {
        config = (await configRes.json()) as Record<string, unknown>;
        configOk = true;
        setConfigLoadOk(true);

        console.info("[PhoneSystem] GET /config agent:", config.agent);

        setAgentName(extractAgentName(config));

        const promptsObj =
          config.prompts && typeof config.prompts === "object"
            ? (config.prompts as Record<string, unknown>)
            : {};
        const agentObj =
          config.agent && typeof config.agent === "object"
            ? (config.agent as Record<string, unknown>)
            : {};
        setAgentRole(
          trimStr(agentObj.role ?? promptsObj.role ?? promptsObj.agent_role),
        );
        const phoneObj =
          config.phone && typeof config.phone === "object"
            ? (config.phone as Record<string, unknown>)
            : {};
        setTwilioNumber(trimStr(phoneObj.twilio_phone_number));
        setOpenaiVoice(trimStr(agentObj.openai_voice));
        // Do NOT seed ElevenLabs voice_id from /config — GET /voice is authority.

        const loadedTz = parseBusinessTimezoneFromConfig(config.business);
        setBusinessTimezone(loadedTz);
        setBusinessTimezoneHydrated(true);
        setBusinessTimezoneLoadError(null);

        const loadedExisting = extractGreetingFromConfig(config, "existing");
        const loadedNew = extractGreetingFromConfig(config, "new");
        setExistingGreeting(loadedExisting);
        setNewCallerGreeting(loadedNew);
        setServerExistingGreeting(loadedExisting);
        setServerNewCallerGreeting(loadedNew);

        const saved = config.sliders;
        if (saved && typeof saved === "object") {
          const next: Record<SliderKey, number> = { ...DEFAULT_50 };
          let hasAny = false;
          for (const k of SLIDER_KEYS) {
            const v = (saved as Record<string, unknown>)[k];
            if (typeof v === "number" && Number.isFinite(v)) {
              next[k] = Math.min(100, Math.max(0, v));
              hasAny = true;
            }
          }
          if (hasAny) {
            setSliders(next);
            setPreset("custom");
          }
        }
      } else {
        console.warn("[PhoneSystem] GET /config failed:", configRes.status);
        setBusinessTimezone(null);
        setBusinessTimezoneHydrated(false);
        setBusinessTimezoneLoadError("Unable to load business timezone. Retry.");
      }

      // ── Prompt blocks (with config fallback) ──
      let displayBlocks: Record<BlockKey, string> = { ...EMPTY_BLOCKS };
      let blocksFetchOk = false;

      if (blocksRes.ok) {
        try {
          const data = await parseJsonOrThrow<{
            success?: boolean;
            blocks?: Record<string, unknown>;
          }>(blocksRes);
          if (data?.success) {
            blocksFetchOk = true;
            let resolved = false;
            try {
              const presRes = await fetch(`${API_BASE}/prompt-blocks/presets`, {
                method: "GET",
                credentials: "include",
              });
              if (presRes.ok) {
                const pres = await parseJsonOrThrow<{
                  success?: boolean;
                  categories?: Record<string, unknown>;
                }>(presRes);
                const cats = pres?.categories || {};
                const resolve = (catKey: string, value: unknown) => {
                  if (typeof value !== "string") return trimStr(value);
                  const cat =
                    cats?.[catKey] && typeof cats[catKey] === "object"
                      ? (cats[catKey] as Record<string, unknown>)
                      : {};
                  const presets =
                    cat.presets && typeof cat.presets === "object"
                      ? (cat.presets as Record<string, unknown>)
                      : {};
                  const hit = presets[value];
                  if (hit && typeof hit === "object" && "prompt" in hit) {
                    return trimStr((hit as { prompt?: unknown }).prompt);
                  }
                  return trimStr(value);
                };
                const raw = data.blocks || {};
                displayBlocks = {
                  system_role: resolve("system_role", raw.system_role),
                  emotional_tone: resolve("emotional_tone", raw.emotional_tone),
                  knowledge_context: resolve("knowledge_context", raw.knowledge_context),
                  safety_boundaries: resolve("safety_boundaries", raw.safety_boundaries),
                };
                resolved = true;
              }
            } catch {
              // non-fatal — fall back to raw values
            }
            if (!resolved) {
              const raw = data.blocks || {};
              displayBlocks = {
                system_role: trimStr(raw.system_role),
                emotional_tone: trimStr(raw.emotional_tone),
                knowledge_context: trimStr(raw.knowledge_context),
                safety_boundaries: trimStr(raw.safety_boundaries),
              };
            }
          }
        } catch (e) {
          console.error("Failed to parse prompt blocks:", e);
          blocksFetchOk = false;
        }
      }

      const blocksHaveContent = Object.values(displayBlocks).some((v) => trimStr(v));
      if ((!blocksFetchOk || !blocksHaveContent) && config) {
        const fromConfig = promptsFromConfig(config);
        displayBlocks = {
          system_role: trimStr(displayBlocks.system_role) || fromConfig.system_role,
          emotional_tone: trimStr(displayBlocks.emotional_tone) || fromConfig.emotional_tone,
          knowledge_context:
            trimStr(displayBlocks.knowledge_context) || fromConfig.knowledge_context,
          safety_boundaries:
            trimStr(displayBlocks.safety_boundaries) || fromConfig.safety_boundaries,
        };
      }

      setServerBlocks(displayBlocks);
      setPromptMain(displayBlocks.system_role);
      setPromptMood(displayBlocks.emotional_tone);
      setPromptKnowledge(displayBlocks.knowledge_context);
      setPromptSafety(displayBlocks.safety_boundaries);
      setPromptsLoadOk(blocksFetchOk || configOk);

      // ── Voice (server only; never fall back to fake editable defaults) ──
      if (voiceRes.ok) {
        try {
          const voiceJson = await voiceRes.json();
          const hydrated = parseVoiceSettingsFromServer(voiceJson?.settings);
          if (hydrated) {
            applyHydratedVoice(hydrated);
          } else {
            markVoiceUnloaded("Unable to load saved voice settings. Retry.");
          }
        } catch (e) {
          console.error("Failed to parse voice settings:", e);
          markVoiceUnloaded("Unable to load saved voice settings. Retry.");
        }
      } else {
        markVoiceUnloaded("Unable to load saved voice settings. Retry.");
      }
    } catch (error) {
      console.error("Failed to load configuration:", error);
      setLoadError("Failed to load your configuration. Please refresh the page or contact support.");
      setBusinessTimezone(null);
      setBusinessTimezoneHydrated(false);
      setBusinessTimezoneLoadError("Unable to load business timezone. Retry.");
    } finally {
      setIsLoadingConfig(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Save — Agent Name
  // ---------------------------------------------------------------------------
  const saveAgentName = async () => {
    const trimmed = agentName.trim();
    if (!trimmed) {
      flash("❌ Agent name cannot be empty.");
      return;
    }
    setIsSavingAgent(true);
    setSaveMessage("");
    try {
      const customerId = getCustomerId();
      if (!customerId) { flash("❌ Error: No customer ID found. Please log in again."); return; }

      const res = await fetch(`${API_BASE}/customers/${customerId}/config`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ agent: { agent_name: trimmed } }),
      });

      if (res.ok) {
        setAgentName(trimmed);
        flash("✅ Agent name saved!");
      } else {
        flash(`Failed to save agent name (Status: ${res.status})`);
      }
    } catch (e) {
      flash(`Error saving agent name: ${e instanceof Error ? e.message : "Network error"}`);
    } finally {
      setIsSavingAgent(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Save — Business Timezone (IANA SSOT via POST /config → business/settings)
  // ---------------------------------------------------------------------------
  const saveBusinessTimezone = async () => {
    if (
      !canSaveBusinessTimezone({
        loaded: businessTimezoneHydrated,
        timezone: businessTimezone,
      })
    ) {
      flash("❌ Select a business timezone after settings load.");
      return;
    }
    setIsSavingTimezone(true);
    setSaveMessage("");
    try {
      const customerId = getCustomerId();
      if (!customerId) {
        flash("❌ Error: No customer ID found. Please log in again.");
        return;
      }

      const res = await fetch(`${API_BASE}/customers/${customerId}/config`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ business: { timezone: businessTimezone } }),
      });

      if (!res.ok) {
        let detail = "";
        try {
          const errBody = await res.json();
          detail = (errBody && (errBody.error || errBody.detail)) || "";
        } catch {
          detail = await res.text().catch(() => "");
        }
        flash(
          detail
            ? `Failed to save timezone (Status: ${res.status}): ${detail}`
            : `Failed to save timezone (Status: ${res.status})`,
        );
        return;
      }

      const getRes = await fetch(`${API_BASE}/customers/${customerId}/config`, {
        method: "GET",
        credentials: "include",
      });
      if (!getRes.ok) {
        flash("✅ Timezone saved, but re-load failed. Retry.");
        return;
      }
      const cfg = (await getRes.json()) as Record<string, unknown>;
      const confirmed = parseBusinessTimezoneFromConfig(cfg.business);
      setBusinessTimezone(confirmed);
      setBusinessTimezoneHydrated(true);
      setBusinessTimezoneLoadError(null);
      flash("✅ Business timezone saved!");
    } catch (e) {
      flash(
        `Error saving timezone: ${e instanceof Error ? e.message : "Network error"}`,
      );
    } finally {
      setIsSavingTimezone(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Save — Greetings
  // ---------------------------------------------------------------------------
  const saveGreetings = async (which: "new" | "existing") => {
    if (which === "new") setIsSavingNewGreeting(true);
    else setIsSavingExistingGreeting(true);
    setSaveMessage("");
    try {
      const customerId = getCustomerId();
      if (!customerId) { flash("❌ Error: No customer ID found. Please log in again."); return; }

      const newVal = trimStr(newCallerGreeting);
      const existingVal = trimStr(existingGreeting);
      const res = await fetch(`${API_BASE}/customers/${customerId}/config`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          agent:
            which === "new"
              ? {
                  new_caller_greeting: newVal,
                  existing_user_greeting: serverExistingGreeting,
                }
              : {
                  existing_user_greeting: existingVal,
                  new_caller_greeting: serverNewCallerGreeting,
                },
        }),
      });

      if (res.ok) {
        if (which === "new") {
          setNewCallerGreeting(newVal);
          setServerNewCallerGreeting(newVal);
        } else {
          setExistingGreeting(existingVal);
          setServerExistingGreeting(existingVal);
        }
        flash(which === "new" ? "✅ New greeting saved!" : "✅ Returning greeting saved!");
      } else {
        flash(`Failed to save greeting (Status: ${res.status})`);
      }
    } catch (e) {
      flash(`Error saving greeting: ${e instanceof Error ? e.message : "Network error"}`);
    } finally {
      if (which === "new") setIsSavingNewGreeting(false);
      else setIsSavingExistingGreeting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Save — Voice
  // ---------------------------------------------------------------------------
  const saveVoice = async () => {
    const voiceId = elevenVoiceId.trim();
    const isCustomVoice = !CATALOG_VOICE_ID_SET.has(voiceId);
    if (isCustomVoice && !voiceId) {
      flash("❌ Enter a custom voice ID before saving.");
      return;
    }
    if (
      !canSaveVoiceSettings({
        voiceSettingsLoaded: voiceLoadOk,
        stability: voiceStability,
        similarity: voiceSimilarity,
      })
    ) {
      flash("❌ Voice settings are not loaded from the server yet.");
      return;
    }
    setIsSavingVoice(true);
    setSaveMessage("");
    try {
      const customerId = getCustomerId();
      if (!customerId) { flash("❌ Error: No customer ID found. Please log in again."); return; }

      const res = await fetch(`${API_BASE}/customers/${customerId}/voice`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          voice_id: voiceId,
          stability: voiceStability,
          similarity_boost: voiceSimilarity,
        }),
      });

      if (res.ok) {
        const ok = await loadVoiceSettings();
        flash(ok ? "✅ Voice saved!" : "✅ Voice saved, but re-load failed. Retry.");
      } else {
        let detail = "";
        try {
          const errBody = await res.json();
          detail = (errBody && (errBody.error || errBody.detail)) || "";
        } catch {
          detail = await res.text().catch(() => "");
        }
        flash(detail
          ? `Failed to save voice (Status: ${res.status}): ${detail}`
          : `Failed to save voice (Status: ${res.status})`);
      }
    } catch (e) {
      flash(`Error saving voice: ${e instanceof Error ? e.message : "Network error"}`);
    } finally {
      setIsSavingVoice(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Save — Prompt block field
  // ---------------------------------------------------------------------------
  const savePromptField = async (field: BlockKey, value: string, label: string) => {
    setIsSavingField(field);
    setSaveMessage("");
    try {
      const trimmed = value.trim();
      const nextBlocks = { ...serverBlocks, [field]: trimmed };
      const res = await fetch(`${API_BASE}/prompt-blocks/save`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ blocks: nextBlocks }),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${txt}`);
      }

      const data = await parseJsonOrThrow<{ success?: boolean; error?: string }>(res);
      if (!data?.success) throw new Error(data?.error || "Unknown error");

      setServerBlocks(nextBlocks);
      flash(`✅ Saved ${label}.`);
    } catch (e: unknown) {
      flash(`❌ Save failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setIsSavingField(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Save — Sliders
  // ---------------------------------------------------------------------------
  const saveSlidersToServer = async () => {
    setIsSavingSliders(true);
    setSaveMessage("");
    try {
      const customerId = getCustomerId();
      if (!customerId) { flash("❌ No customer ID found. Please log in again."); return; }

      const res = await fetch(`${API_BASE}/customers/${customerId}/config`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sliders }),
      });

      if (res.status === 401 || res.status === 403) {
        flash("❌ Unauthorized. Please log in again.");
        return;
      }

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        flash(`❌ Save failed: HTTP ${res.status} ${txt}`);
        return;
      }

      flash("✅ Saved sliders.");
    } catch (e: unknown) {
      flash(`❌ Save failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setIsSavingSliders(false);
    }
  };

  // ---------------------------------------------------------------------------
  // AI Generate
  // ---------------------------------------------------------------------------
  const generatePrompt = async (field: string) => {
    if (!generateBizType.trim()) return;
    setIsGenerating(true);
    try {
      const response = await fetch(`${API_BASE}/customers/${getCustomerId()}/generate-prompt`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          field,
          business_type: generateBizType.trim(),
          agent_name: agentName || "your assistant",
        }),
      });
      const data = await response.json();
      const text = data?.text?.trim();
      if (text) {
        if (field === "system_role") setPromptMain(text);
        else if (field === "emotional_tone") setPromptMood(text);
        else if (field === "safety_boundaries") setPromptSafety(text);
      }
      setGenerateModalOpen(null);
      setGenerateBizType("");
    } catch (e) {
      flash("❌ Generation failed — please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Save all (Personality)
  // ---------------------------------------------------------------------------
  // Each savePromptField call posts every block from its render's serverBlocks, so the
  // next step must start only after the previous step's state has rendered.
  useEffect(() => {
    const step = saveAllQueue[0];
    if (!step || saveAllInFlightRef.current === saveAllQueue) return;
    saveAllInFlightRef.current = saveAllQueue;
    setLastSavedKey(step);
    const run =
      step === "sliders"
        ? saveSlidersToServer()
        : step === "system_role"
          ? savePromptField("system_role", promptMain, "Main prompt")
          : step === "emotional_tone"
            ? savePromptField("emotional_tone", promptMood, "Mood prompt")
            : savePromptField("knowledge_context", promptKnowledge, "Knowledge prompt");
    void run.finally(() => setSaveAllQueue((q) => q.slice(1)));
  }, [saveAllQueue]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSavingAll = saveAllQueue.length > 0;
  const saveAllDisabled =
    isSavingAll || !!isSavingField || isSavingSliders || !configLoadOk || !promptsLoadOk;

  // ---------------------------------------------------------------------------
  // Slider helpers
  // ---------------------------------------------------------------------------
  const setSlider = (k: SliderKey, v: number) => {
    setSliders((s) => ({ ...s, [k]: v }));
    if (preset !== "custom") setPreset("custom");
  };

  const applyPreset = (name: string) => {
    const next = PRESETS[name] || PRESETS.balanced;
    setPreset(name);
    setSliders({ ...next });
  };

  const phoneIsActive = Boolean(twilioNumber.trim());

  const assignedVoiceDisplay = useMemo(
    () => assignedVoiceSummary(elevenVoiceId, openaiVoice),
    [elevenVoiceId, openaiVoice],
  );

  const isCustomVoiceMode = !CATALOG_VOICE_ID_SET.has(elevenVoiceId.trim());
  const customVoiceIdEmpty = isCustomVoiceMode && !elevenVoiceId.trim();
  const agentNameEmpty = !agentName.trim();
  const tzGroups = useMemo(() => listBusinessTimezoneOptionGroups(), []);
  const tzOptions = useMemo(() => [...tzGroups.friendly, ...tzGroups.all], [tzGroups]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <CoreLayout>
      <PageContainer>
        {/* ── Header ── */}
        <PageHeader
          title="Phone System"
          subtitle="Agent configuration, greetings, voice, personality & safety rules."
        />

        {/* ── Tab nav ── */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-white/10">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium transition-colors -mb-px border-b-2 ${
                  isActive
                    ? "text-white border-[#8a2be2]"
                    : "text-gray-400 hover:text-gray-200 border-transparent"
                }`}
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── Load error banner ── */}
        <div className="w-full">
          {loadError && (
            <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-3">
              <i className="ri-error-warning-line text-lg" />
              <span>{loadError}</span>
              <button
                onClick={() => { setLoadError(null); loadAll(); }}
                className="ml-auto text-xs underline hover:text-red-300"
              >
                Try again
              </button>
            </div>
          )}

          {/* ── Loading state ── */}
          {isLoadingConfig ? (
            <div className="text-center py-12 text-purple-300"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Loading configuration…
            </div>
          ) : (
            <>

            {/* ================================================================
                TAB 1 — AGENT
            ================================================================ */}
            {activeTab === "agent" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                {/* Agent Name */}
                <CardShell icon="ri-robot-line" title="Agent Name">
                  <label className="block text-xs text-gray-400 mb-1">
                    The name your agent introduces itself with.
                  </label>
                  <input
                    type="text"
                    value={agentName}
                    onChange={(e) => setAgentName(e.target.value)}
                    placeholder="e.g. Aria"
                    className="w-full px-4 py-3 rounded-lg bg-black/40 border border-purple-500/30 text-white focus:outline-none focus:border-purple-500"
                  />
                  {agentNameEmpty && configLoadOk && (
                    <p
                      className="text-xs text-orange-300 mt-1"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      Agent name cannot be empty.
                    </p>
                  )}
                  <LoadGuardNotice failed={!configLoadOk} />
                  <SaveButton
                    onClick={() => {
                      setLastSavedKey("agentName");
                      void saveAgentName();
                    }}
                    disabled={isSavingAgent || !configLoadOk || agentNameEmpty}
                    saving={isSavingAgent}
                    label="💾 Save Agent Name"
                    message={messageFor("agentName")}
                  />
                </CardShell>

                {/* Business Timezone — canonical IANA for temporal awareness */}
                <CardShell icon="ri-time-zone-line" title="Business Timezone">
                  <label className="block text-xs text-gray-400 mb-1">
                    Used so your receptionist knows the local date and time when talking to callers.
                  </label>
                  {isLoadingConfig && !businessTimezoneHydrated && !businessTimezoneLoadError && (
                    <p className="text-sm text-gray-400 mb-3">Loading business timezone…</p>
                  )}
                  {businessTimezoneLoadError && (
                    <div className="mb-3 flex flex-wrap items-center gap-3">
                      <p className="text-sm text-amber-300">{businessTimezoneLoadError}</p>
                      <button
                        type="button"
                        onClick={() => {
                          void loadAll();
                        }}
                        className="px-3 py-1.5 text-xs rounded-lg border border-purple-500/40 text-purple-200 hover:bg-purple-500/10"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                  {businessTimezoneHydrated && (
                    <p className="text-xs text-gray-500 mb-2">
                      {businessTimezone
                        ? `Current: ${
                            tzOptions.find((o) => o.value === businessTimezone)?.label ??
                            businessTimezone
                          }`
                        : "Not set yet. Choose a timezone and save."}
                    </p>
                  )}
                  <fieldset
                    disabled={!businessTimezoneHydrated || isSavingTimezone}
                    className={
                      !businessTimezoneHydrated ? "opacity-50 pointer-events-none" : undefined
                    }
                  >
                    <select
                      value={timezoneSelectValue(businessTimezone, tzOptions)}
                      onChange={(e) => {
                        const v = e.target.value.trim();
                        setBusinessTimezone(v || null);
                      }}
                      className="w-full px-4 py-3 rounded-lg bg-black/40 border border-purple-500/30 text-white focus:outline-none focus:border-purple-500"
                    >
                      <option value="">Select timezone…</option>
                      {tzGroups.friendly.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                      {tzGroups.all.length > 0 && (
                        <optgroup label="All timezones">
                          {tzGroups.all.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {businessTimezone &&
                        !tzOptions.some((o) => o.value === businessTimezone) && (
                          <option value={businessTimezone}>{businessTimezone}</option>
                        )}
                    </select>
                    {businessTimezone && (
                      <p className="mt-1 text-xs text-gray-500 font-mono">{businessTimezone}</p>
                    )}
                  </fieldset>
                  <LoadGuardNotice
                    failed={!businessTimezoneHydrated && !businessTimezoneLoadError}
                  />
                  <SaveButton
                    onClick={() => {
                      setLastSavedKey("timezone");
                      void saveBusinessTimezone();
                    }}
                    disabled={
                      isSavingTimezone ||
                      !canSaveBusinessTimezone({
                        loaded: businessTimezoneHydrated,
                        timezone: businessTimezone,
                      })
                    }
                    saving={isSavingTimezone}
                    label="💾 Save Business Timezone"
                    message={messageFor("timezone")}
                  />
                </CardShell>

                {/* Save handler removed while this card is hidden; restore from git history when the backend persists agent.role. */}
                {/* Agent Role — hidden for now
                <CardShell icon="ri-briefcase-line" title="Agent Role">
                  <label className="block text-xs text-gray-400 mb-1">
                    A short description of what this agent does.
                  </label>
                  <input
                    type="text"
                    value={agentRole}
                    onChange={(e) => setAgentRole(e.target.value)}
                    placeholder="e.g. Front desk receptionist for Acme Insurance"
                    className="w-full px-4 py-3 rounded-lg bg-black/40 border border-purple-500/30 text-white focus:outline-none focus:border-purple-500"
                  />
                  <SaveButton
                    onClick={saveAgentRole}
                    disabled={isSavingRole}
                    saving={isSavingRole}
                    label="💾 Save Agent Role"
                  />
                </CardShell>
                */}

                {/* Assigned Phone Number */}
                <CardShell icon="ri-phone-line" title="Assigned Phone Number">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <p
                      className={
                        phoneIsActive
                          ? "text-lg font-semibold text-white font-mono tracking-wide min-w-0 break-all"
                          : "text-sm font-normal text-blue-200/90 leading-relaxed min-w-0"
                      }
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      {phoneIsActive ? twilioNumber : PHONE_PROVISIONING_PLACEHOLDER}
                    </p>
                    {phoneIsActive ? (
                      <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 shrink-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full bg-white/5 text-gray-400 shrink-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
                        Unassigned
                      </span>
                    )}
                  </div>

                  {phoneIsActive ? (
                    <p
                      className="text-sm text-gray-400 leading-relaxed mb-4"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      {PHONE_FORWARDING_HINT}
                    </p>
                  ) : null}

                  {(agentName.trim() || assignedVoiceDisplay || agentRole.trim()) && (
                    <div
                      className="space-y-2 mb-4"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      {agentName.trim() && (
                        <div className="flex items-baseline gap-3 py-1.5 border-b border-white/5">
                          <span className="text-xs text-gray-500 uppercase tracking-wider w-20 shrink-0">
                            Agent
                          </span>
                          <span className="text-sm text-white">{agentName}</span>
                        </div>
                      )}
                      {assignedVoiceDisplay && (
                        <div className="flex items-baseline gap-3 py-1.5 border-b border-white/5">
                          <span className="text-xs text-gray-500 uppercase tracking-wider w-20 shrink-0">
                            Voice
                          </span>
                          <span className="text-sm text-white">{assignedVoiceDisplay}</span>
                        </div>
                      )}
                      {agentRole.trim() && (
                        <div className="flex items-baseline gap-3 py-1.5 border-b border-white/5 last:border-0">
                          <span className="text-xs text-gray-500 uppercase tracking-wider w-20 shrink-0">
                            Role
                          </span>
                          <span className="text-sm text-white">{agentRole}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {phoneIsActive ? (
                    <p
                      className="text-xs text-gray-500 leading-relaxed"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      Managed by our team. Contact support to change your number.
                    </p>
                  ) : null}
                </CardShell>
              </div>
            )}

            {/* ================================================================
                TAB 2 — GREETINGS
            ================================================================ */}
            {activeTab === "greetings" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* New Caller */}
                <CardShell icon="ri-chat-smile-2-line" title="New Caller Greeting">
                  <label htmlFor="greeting-new" className="sr-only">
                    New Caller Greeting
                  </label>
                  <textarea
                    id="greeting-new"
                    value={newCallerGreeting}
                    onChange={(e) => setNewCallerGreeting(e.target.value)}
                    placeholder="Greeting for brand new callers…"
                    className="w-full min-h-[220px] px-4 py-3 rounded-lg bg-black/40 border border-purple-500/30 text-white focus:outline-none focus:border-purple-500"
                  />
                  <div className="mt-2 text-xs text-gray-400">
                    You can use <code className="text-purple-400">{'{agent_name}'}</code> (your
                    receptionist&apos;s name) and{" "}
                    <code className="text-purple-400">{'{time_greeting}'}</code> (good morning /
                    afternoon / evening).
                  </div>
                  <LoadGuardNotice failed={!configLoadOk} />
                  <SaveButton
                    onClick={() => {
                      setLastSavedKey("newGreeting");
                      void saveGreetings("new");
                    }}
                    disabled={isSavingNewGreeting || !configLoadOk}
                    saving={isSavingNewGreeting}
                    label="💾 Save New Greeting"
                    message={messageFor("newGreeting")}
                  />
                </CardShell>

                {/* Returning Caller */}
                <CardShell icon="ri-chat-voice-line" title="Returning Caller Greeting">
                  <label htmlFor="greeting-existing" className="sr-only">
                    Returning Caller Greeting
                  </label>
                  <textarea
                    id="greeting-existing"
                    value={existingGreeting}
                    onChange={(e) => setExistingGreeting(e.target.value)}
                    placeholder="Greeting for returning callers…"
                    className="w-full min-h-[220px] px-4 py-3 rounded-lg bg-black/40 border border-purple-500/30 text-white focus:outline-none focus:border-purple-500"
                  />
                  <div className="mt-2 text-xs text-gray-400">
                    You can use <code className="text-purple-400">{'{agent_name}'}</code> and{" "}
                    <code className="text-purple-400">{'{user_name}'}</code> (the caller&apos;s name,
                    when known).
                  </div>
                  <LoadGuardNotice failed={!configLoadOk} />
                  <SaveButton
                    onClick={() => {
                      setLastSavedKey("existingGreeting");
                      void saveGreetings("existing");
                    }}
                    disabled={isSavingExistingGreeting || !configLoadOk}
                    saving={isSavingExistingGreeting}
                    label="💾 Save Returning Greeting"
                    message={messageFor("existingGreeting")}
                  />
                </CardShell>
              </div>
            )}

            {/* ================================================================
                TAB 3 — VOICE
            ================================================================ */}
            {activeTab === "voice" && (
              <div className="max-w-2xl">
                <CardShell icon="ri-mic-line" title="ElevenLabs Voice">
                  {(isLoadingConfig || voiceSettingsLoading) &&
                    !voiceLoadOk &&
                    !voiceSettingsLoadError && (
                      <p className="text-sm text-gray-400 mb-4">Loading voice settings…</p>
                    )}
                  {voiceSettingsLoadError && (
                    <div className="mb-4 flex flex-wrap items-center gap-3">
                      <p className="text-sm text-amber-300">{voiceSettingsLoadError}</p>
                      <button
                        type="button"
                        onClick={() => {
                          void loadVoiceSettings();
                        }}
                        className="px-3 py-1.5 text-xs rounded-lg border border-purple-500/40 text-purple-200 hover:bg-purple-500/10"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                  {voiceLoadOk && formatDateTime(voiceSettingsUpdatedAt) && (
                    <p className="text-xs text-gray-500 mb-3">
                      Last saved {formatDateTime(voiceSettingsUpdatedAt)}
                    </p>
                  )}
                  <fieldset
                    disabled={!voiceLoadOk || isSavingVoice}
                    className={!voiceLoadOk ? "opacity-50 pointer-events-none" : undefined}
                  >
                  <label className="block text-xs text-gray-400 mb-1">Voice</label>
                  <select
                    value={CATALOG_VOICE_ID_SET.has(elevenVoiceId) ? elevenVoiceId : CUSTOM_VOICE_SELECT}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === CUSTOM_VOICE_SELECT) {
                        if (CATALOG_VOICE_ID_SET.has(elevenVoiceId)) setElevenVoiceId("");
                        return;
                      }
                      setElevenVoiceId(v);
                    }}
                    className="w-full px-4 py-3 rounded-lg bg-black/40 border border-purple-500/30 text-white focus:outline-none focus:border-purple-500"
                  >
                    {ELEVENLABS_VOICE_CATALOG.map((v) => (
                      <option key={v.id} value={v.id}>{v.label}</option>
                    ))}
                    <option value={CUSTOM_VOICE_SELECT}>Custom voice ID…</option>
                  </select>
                  {!CATALOG_VOICE_ID_SET.has(elevenVoiceId) && (
                    <div className="mt-3">
                      <label className="block text-xs text-gray-400 mb-1">Custom voice ID</label>
                      <input
                        type="text"
                        value={elevenVoiceId}
                        onChange={(e) => setElevenVoiceId(e.target.value)}
                        placeholder="Paste ElevenLabs voice_id"
                        className="w-full px-4 py-3 rounded-lg bg-black/40 border border-purple-500/30 text-white focus:outline-none focus:border-purple-500"
                      />
                    </div>
                  )}

                  <div className="mt-4 space-y-5">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs text-gray-400">
                          Voice Consistency / Stability
                        </label>
                        <span className="text-xs" style={{ color: "#fbbf24" }}>
                          {voiceStability === null ? "—" : voiceStability.toFixed(2)}
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.01}
                        value={voiceStability ?? 0}
                        onChange={(e) =>
                          setVoiceStability(parseFloat(e.target.value) || 0)
                        }
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>{VOICE_STABILITY_LABEL_LEFT}</span>
                        <span>{VOICE_STABILITY_LABEL_RIGHT}</span>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs text-gray-400">
                          Voice Naturalness / Similarity
                        </label>
                        <span className="text-xs" style={{ color: "#fbbf24" }}>
                          {voiceSimilarity === null ? "—" : voiceSimilarity.toFixed(2)}
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.01}
                        value={voiceSimilarity ?? 0}
                        onChange={(e) =>
                          setVoiceSimilarity(parseFloat(e.target.value) || 0)
                        }
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>Natural</span>
                        <span>Precise</span>
                      </div>
                    </div>
                  </div>
                  </fieldset>

                  {customVoiceIdEmpty && voiceLoadOk && (
                    <p
                      className="text-xs text-orange-300 mt-1"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      Enter a custom voice ID before saving.
                    </p>
                  )}
                  <LoadGuardNotice failed={!voiceLoadOk && !voiceSettingsLoadError} />

                  <SaveButton
                    onClick={() => {
                      setLastSavedKey("voice");
                      void saveVoice();
                    }}
                    disabled={
                      isSavingVoice ||
                      customVoiceIdEmpty ||
                      !canSaveVoiceSettings({
                        voiceSettingsLoaded: voiceLoadOk,
                        stability: voiceStability,
                        similarity: voiceSimilarity,
                      })
                    }
                    saving={isSavingVoice}
                    label="💾 Save Voice"
                    message={messageFor("voice")}
                  />
                </CardShell>
              </div>
            )}

            {/* ================================================================
                TAB 4 — PERSONALITY
            ================================================================ */}
            {activeTab === "personality" && (
              <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Main System Prompt */}
                  <CardShell icon="ri-file-text-line" title="Main System Prompt">
                    <label htmlFor="prompt-system-role" className="sr-only">
                      Main System Prompt
                    </label>
                    <textarea
                      id="prompt-system-role"
                      className="w-full min-h-[220px] px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                      value={promptMain}
                      onChange={(e) => setPromptMain(e.target.value)}
                      placeholder="Describe the agent's core identity and purpose…"
                    />
                    <button
                      onClick={(e) => {
                        generateButtonRef.current = e.currentTarget;
                        setGenerateModalOpen("system_role");
                      }}
                      className="mt-3 w-full py-2 rounded-lg text-white font-semibold text-sm"
                      style={{ background: "rgba(138, 43, 226, 0.25)", border: "1px solid rgba(138, 43, 226, 0.35)", fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      ✨ AI Generate
                    </button>
                    <LoadGuardNotice failed={!promptsLoadOk} />
                    <SaveButton
                      onClick={() => {
                        setLastSavedKey("system_role");
                        void savePromptField("system_role", promptMain, "Main prompt");
                      }}
                      disabled={isSavingField === "system_role" || !promptsLoadOk}
                      saving={isSavingField === "system_role"}
                      label="💾 Save Main Prompt"
                      message={messageFor("system_role")}
                    />
                  </CardShell>

                  {/* Mood / Emotional Tone */}
                  <CardShell icon="ri-emotion-happy-line" title="Mood / Emotional Tone">
                    <label htmlFor="prompt-emotional-tone" className="sr-only">
                      Mood / Emotional Tone
                    </label>
                    <textarea
                      id="prompt-emotional-tone"
                      className="w-full min-h-[220px] px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                      value={promptMood}
                      onChange={(e) => setPromptMood(e.target.value)}
                      placeholder="Describe the agent's emotional tone and communication style…"
                    />
                    <button
                      onClick={(e) => {
                        generateButtonRef.current = e.currentTarget;
                        setGenerateModalOpen("emotional_tone");
                      }}
                      className="mt-3 w-full py-2 rounded-lg text-white font-semibold text-sm"
                      style={{ background: "rgba(138, 43, 226, 0.25)", border: "1px solid rgba(138, 43, 226, 0.35)", fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      ✨ AI Generate
                    </button>
                    <LoadGuardNotice failed={!promptsLoadOk} />
                    <SaveButton
                      onClick={() => {
                        setLastSavedKey("emotional_tone");
                        void savePromptField("emotional_tone", promptMood, "Mood prompt");
                      }}
                      disabled={isSavingField === "emotional_tone" || !promptsLoadOk}
                      saving={isSavingField === "emotional_tone"}
                      label="💾 Save Mood Prompt"
                      message={messageFor("emotional_tone")}
                    />
                  </CardShell>

                  {/* Knowledge Context */}
                  <CardShell icon="ri-book-open-line" title="Knowledge Prompt">
                    <label htmlFor="prompt-knowledge-context" className="sr-only">
                      Knowledge Prompt
                    </label>
                    <textarea
                      id="prompt-knowledge-context"
                      className="w-full min-h-[220px] px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                      value={promptKnowledge}
                      onChange={(e) => setPromptKnowledge(e.target.value)}
                      placeholder="Describe what the agent should know about your business, services, and policies…"
                    />
                    <button
                      onClick={(e) => {
                        generateButtonRef.current = e.currentTarget;
                        setGenerateModalOpen("knowledge_context");
                      }}
                      className="mt-3 w-full py-2 rounded-lg text-white font-semibold text-sm"
                      style={{ background: "rgba(138, 43, 226, 0.25)", border: "1px solid rgba(138, 43, 226, 0.35)", fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      ✨ AI Generate
                    </button>
                    <LoadGuardNotice failed={!promptsLoadOk} />
                    <SaveButton
                      onClick={() => {
                        setLastSavedKey("knowledge_context");
                        void savePromptField("knowledge_context", promptKnowledge, "Knowledge prompt");
                      }}
                      disabled={isSavingField === "knowledge_context" || !promptsLoadOk}
                      saving={isSavingField === "knowledge_context"}
                      label="💾 Save Knowledge Prompt"
                      message={messageFor("knowledge_context")}
                    />
                  </CardShell>

                {/* Style Sliders — full width */}
                <div
                  className={`md:col-span-2 p-6 rounded-2xl ${cardBorderClassName}`}
                  style={{
                    background: "rgba(26, 26, 36, 0.75)",
                  }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-purple-600 to-orange-600">
                      <i className="ri-equalizer-line text-white text-xl" />
                    </div>
                    <h2
                      className="text-xl font-semibold text-white"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      Style Sliders
                    </h2>
                  </div>

                  {/* Preset buttons */}
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(PRESETS).map((name) => (
                        <button
                          key={name}
                          onClick={() => applyPreset(name)}
                          className="px-4 py-2 rounded-lg text-sm font-semibold"
                          style={{
                            background:
                              preset === name
                                ? "rgba(138, 43, 226, 0.28)"
                                : "rgba(255,255,255,0.03)",
                            border: "1px solid rgba(138, 43, 226, 0.2)",
                            color: preset === name ? "#ffffff" : "#aaaaaa",
                            fontFamily: "'Space Grotesk', sans-serif",
                          }}
                        >
                          {PRESET_LABELS[name] ?? name}
                        </button>
                      ))}
                    </div>

                    <div className="flex flex-col items-end">
                      <button
                        onClick={() => {
                          setLastSavedKey("sliders");
                          void saveSlidersToServer();
                        }}
                        disabled={isSavingSliders || !!isSavingField || !configLoadOk}
                        className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:cursor-not-allowed"
                        style={{
                          background: "linear-gradient(90deg, #8a2be2, #ff6a00)",
                          opacity: isSavingSliders || !configLoadOk ? 0.6 : 1,
                          fontFamily: "'Space Grotesk', sans-serif",
                        }}
                      >
                        {isSavingSliders ? "Saving…" : "💾 Save Sliders"}
                      </button>
                      <SaveMessage message={messageFor("sliders")} className="mt-2 text-right" />
                    </div>
                  </div>
                  <LoadGuardNotice failed={!configLoadOk} />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SliderRow k="warmth"     label="Warmth"     value={sliders.warmth}     onChange={setSlider} />
                    <SliderRow k="formality"  label="Formality"  value={sliders.formality}  onChange={setSlider} />
                    <SliderRow k="humor"      label="Humor"      value={sliders.humor}      onChange={setSlider} />
                    <SliderRow k="directness" label="Directness" value={sliders.directness} onChange={setSlider} />
                    <SliderRow k="empathy"    label="Empathy"    value={sliders.empathy}    onChange={setSlider} />
                    <SliderRow k="confidence" label="Confidence" value={sliders.confidence} onChange={setSlider} />
                    <SliderRow k="curiosity"  label="Curiosity"  value={sliders.curiosity}  onChange={setSlider} />
                    <SliderRow k="patience"   label="Patience"   value={sliders.patience}   onChange={setSlider} />
                  </div>

                  <button
                    className="mt-5 w-full px-4 py-3 rounded-lg text-white font-semibold"
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(138, 43, 226, 0.2)",
                      fontFamily: "'Space Grotesk', sans-serif",
                    }}
                    onClick={() => setShowAdvanced((v) => !v)}
                  >
                    {showAdvanced ? "Hide Advanced Sliders" : "Show Advanced Sliders"}
                  </button>

                  {showAdvanced && (
                    <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <SliderRow k="polish"         label="Polish / Fluency"      value={sliders.polish}         onChange={setSlider} />
                      <SliderRow k="intensity"      label="Emotional Intensity"   value={sliders.intensity}      onChange={setSlider} />
                      <SliderRow k="caution"        label="Caution / Guardrails"  value={sliders.caution}        onChange={setSlider} />
                      <SliderRow k="jargon"         label="Jargon / Technicality" value={sliders.jargon}         onChange={setSlider} />
                      <SliderRow k="metaAwareness"  label="Meta Awareness"        value={sliders.metaAwareness}  onChange={setSlider} />
                      <SliderRow k="memory"         label="Memory Emphasis"       value={sliders.memory}         onChange={setSlider} />
                      <SliderRow k="risk"           label="Risk Taking"           value={sliders.risk}           onChange={setSlider} />
                      <SliderRow k="sarcasm"        label="Sarcasm"               value={sliders.sarcasm}        onChange={setSlider} />
                    </div>
                  )}
                </div>
              </div>

              <div className="sticky bottom-0 z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-[#0b0b0f]/90 backdrop-blur border-t border-white/10 flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">Each card saves on its own, or save everything at once.</p>
                <button
                  type="button"
                  onClick={() => setSaveAllQueue([...SAVE_ALL_STEPS])}
                  disabled={saveAllDisabled}
                  className="w-auto px-4 py-2 rounded-lg font-semibold transition-all"
                  style={{
                    background: saveAllDisabled
                      ? "rgba(138, 43, 226, 0.4)"
                      : "linear-gradient(90deg, #8a2be2, #ff6a00)",
                    color: "white",
                    cursor: saveAllDisabled ? "not-allowed" : "pointer",
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}
                >
                  Save all personality
                </button>
              </div>
              </div>
            )}

            {/* ================================================================
                TAB 5 — SAFETIES
            ================================================================ */}
            {activeTab === "safeties" && (
              <div className="max-w-2xl space-y-4">
                {/* Info card */}
                <div
                  className="p-5 rounded-xl flex items-start gap-4"
                  style={{
                    background: "rgba(138, 43, 226, 0.08)",
                    border: "1px solid rgba(138, 43, 226, 0.25)",
                  }}
                >
                  <i className="ri-shield-check-line text-purple-400 text-2xl mt-0.5 shrink-0" />
                  <p
                    className="text-sm text-gray-300 leading-relaxed"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    Safety rules prevent the agent from discussing competitors, making
                    promises outside your policy, or disclosing internal information.
                  </p>
                </div>

                <CardShell icon="ri-shield-check-line" title="Safety Prompt">
                  <label htmlFor="prompt-safety-boundaries" className="sr-only">
                    Safety Prompt
                  </label>
                  <label className="block text-xs text-gray-400 mb-1">
                    Guardrails only — describe hard limits and escalation rules.
                  </label>
                  <textarea
                    id="prompt-safety-boundaries"
                    className="w-full min-h-[220px] px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    value={promptSafety}
                    onChange={(e) => setPromptSafety(e.target.value)}
                    placeholder="e.g. Do not provide legal or medical advice. If unsure, escalate to a human agent."
                  />
                  <button
                    onClick={(e) => {
                      generateButtonRef.current = e.currentTarget;
                      setGenerateModalOpen("safety_boundaries");
                    }}
                    className="mt-3 w-full py-2 rounded-lg text-white font-semibold text-sm"
                    style={{ background: "rgba(138, 43, 226, 0.25)", border: "1px solid rgba(138, 43, 226, 0.35)", fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    ✨ AI Generate
                  </button>
                  <LoadGuardNotice failed={!promptsLoadOk} />
                  <SaveButton
                    onClick={() => {
                      setLastSavedKey("safety_boundaries");
                      void savePromptField("safety_boundaries", promptSafety, "Safety prompt");
                    }}
                    disabled={isSavingField === "safety_boundaries" || !promptsLoadOk}
                    saving={isSavingField === "safety_boundaries"}
                    label="💾 Save Safety Prompt"
                    message={messageFor("safety_boundaries")}
                  />
                </CardShell>
              </div>
            )}

            </>
          )}
        </div>
      </PageContainer>
      <Modal
        open={!!generateModalOpen}
        onClose={() => { setGenerateModalOpen(null); setGenerateBizType(""); }}
        title="✨ AI Generate"
        maxWidthClassName="max-w-md"
        returnFocusTo={generateButtonRef}
      >
        {generateModalOpen && (
          <>
            <p className="text-sm text-gray-400 mb-4">What type of business is this agent for?</p>
            <input
              type="text"
              value={generateBizType}
              onChange={(e) => setGenerateBizType(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                setLastSavedKey(null);
                void generatePrompt(generateModalOpen);
              }}
              placeholder="e.g. dental office, law firm, HVAC company"
              className="w-full px-4 py-3 rounded-lg bg-black/40 border border-purple-500/30 text-white focus:outline-none focus:border-purple-500 mb-4"
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setLastSavedKey(null);
                  void generatePrompt(generateModalOpen);
                }}
                disabled={!generateBizType.trim() || isGenerating}
                className="flex-1 py-3 rounded-lg font-semibold text-white transition-all"
                style={{
                  background: (!generateBizType.trim() || isGenerating) ? "rgba(138,43,226,0.4)" : "linear-gradient(90deg, #8a2be2, #ff6a00)",
                  cursor: (!generateBizType.trim() || isGenerating) ? "not-allowed" : "pointer",
                  fontFamily: "'Space Grotesk', sans-serif",
                }}
              >
                {isGenerating ? "Generating…" : "Generate"}
              </button>
              <button
                onClick={() => { setGenerateModalOpen(null); setGenerateBizType(""); }}
                className="px-4 py-3 rounded-lg border border-white/10 text-white/60 hover:text-white"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </Modal>
    </CoreLayout>
  );
}
